import type { StoryCtx } from './ctx.js';
import { evenRanks, rankBetween } from './narrative.js';
import type { Entity, LibraryGroup } from './types.js';

/**
 * Raster der Kategorien (Rahmen) auf der Pinnwand der Bibliothek. Die Zugehörigkeit einer Karte ergibt sich aus der Lage (Mitte im
 * Rahmen); Web und Server (z. B. das Tutorial) legen Karten deshalb nach demselben Raster in die Rahmen.
 */

/** Raster der Plätze in einem Rahmen (Abstand der Kartenmitten, Rand, Platz für den Namen oben) */
export const SLOT = { w: 260, h: 140, padX: 24, top: 60 };
/** halbe Karte auf der mittleren Zoomstufe der Pinnwand (232 × 78) */
export const CARD_HALF = { w: 116, h: 39 };

/** Größe eines Rahmens für `columns` × `rows` Karten. */
export const frameSize = (columns: number, rows: number): { w: number; h: number } => ({
  w: SLOT.padX * 2 + CARD_HALF.w * 2 + (columns - 1) * SLOT.w,
  h: SLOT.top + CARD_HALF.h * 2 + (rows - 1) * SLOT.h + SLOT.padX,
});

/** Spalten des Rasters in einem Rahmen der Breite `w` (mindestens eine Karte breit). */
export const frameColumns = (w: number): number => Math.max(1, Math.floor((Math.max(w, frameSize(1, 1).w) - SLOT.padX * 2 - CARD_HALF.w * 2) / SLOT.w) + 1);

/** Mitte des Platzes `i` (zeilenweise gezählt) in einem Rahmen. */
export const slotCenter = (g: { x: number; y: number; w: number }, i: number): { x: number; y: number } => {
  const columns = frameColumns(g.w);
  return { x: g.x + SLOT.padX + CARD_HALF.w + (i % columns) * SLOT.w, y: g.y + SLOT.top + CARD_HALF.h + Math.floor(i / columns) * SLOT.h };
};

/** Farben neuer Kategorien (der Reihe nach) */
export const CATEGORY_COLORS = ['#38bdf8', '#a78bfa', '#f472b6', '#34d399', '#fbbf24', '#fb923c', '#60a5fa', '#f87171'];

// ---------------------------------------------------------------------------
// Lage der Karten auf der Pinnwand und ihre Kategorien (Web und Server, z. B. beim Import aus einem anderen Projekt)
// ---------------------------------------------------------------------------

/** Raster, in dem noch nicht platzierte Einträge automatisch angeordnet werden (Abstand der Kartenmitten) */
export const LIB_CELL = { w: 250, h: 130 };

/** Ein Eintrag auf der Bibliothekskarte samt der Lage, in der er gezeigt wird. */
export interface PlacedEntity {
  entity: Entity;
  x: number;
  y: number;
  /** false: noch keine eigene Lage, automatisch angeordnet (wird erst beim Verschieben gespeichert) */
  placed: boolean;
}

/** Einträge, die auf der Bibliothekskarte erscheinen: Personen, Orte und Dinge (keine Foundationals). */
export const libraryEntities = (ctx: StoryCtx): Entity[] =>
  ctx.story.entities.filter((e) => {
    const category = ctx.types.get(e.typeId)?.category;
    return category === 'object' || category === 'place';
  });

/**
 * Lage aller Einträge: gespeicherte Positionen bleiben, die übrigen werden zeilenweise unterhalb der übrigen Einträge und unterhalb aller
 * Rahmen in einem Raster angeordnet (damit sie nirgends übereinander liegen, auffindbar sind und in keine Kategorie geraten).
 */
export function layoutLibrary(ctx: StoryCtx): PlacedEntity[] {
  const all = libraryEntities(ctx);
  const placed = all.filter((e) => e.x !== null && e.y !== null);
  const loose = all.filter((e) => e.x === null || e.y === null);
  const out: PlacedEntity[] = placed.map((entity) => ({ entity, x: entity.x!, y: entity.y!, placed: true }));
  const frames = ctx.story.groups;
  const left = placed.length > 0 ? Math.min(...placed.map((e) => e.x!)) : frames.length > 0 ? Math.min(...frames.map((g) => g.x + SLOT.padX + CARD_HALF.w)) : 0;
  const bottom = Math.max(
    placed.length > 0 ? Math.max(...placed.map((e) => e.y!)) + LIB_CELL.h * 1.5 : 0,
    frames.length > 0 ? Math.max(...frames.map((g) => g.y + g.h)) + LIB_CELL.h * 0.75 : 0,
  );
  const columns = 5;
  loose.forEach((entity, i) => out.push({ entity, x: left + (i % columns) * LIB_CELL.w, y: bottom + Math.floor(i / columns) * LIB_CELL.h, placed: false }));
  return out;
}

export const inGroup = (g: LibraryGroup, p: { x: number; y: number }): boolean => p.x >= g.x && p.x <= g.x + g.w && p.y >= g.y && p.y <= g.y + g.h;

/** Einträge, die zu einer Gruppe gehören: ihre Kartenmitte liegt im Rahmen. */
export const membersOf = (g: LibraryGroup, placed: PlacedEntity[]): PlacedEntity[] => placed.filter((p) => inGroup(g, p));

/** Rahmen (Kategorien) eines Bereichs; ältere Rahmen ohne Bereich gehören zu Person. */
export function groupsOfType(ctx: StoryCtx, typeId: string): LibraryGroup[] {
  const person = ctx.story.types.find((t) => t.builtin && t.name === 'Person')?.id;
  return ctx.story.groups.filter((g) => (g.typeId ?? person) === typeId);
}

/** Kategorie eines Punktes: der kleinste Rahmen, in dem er liegt (bei Überlappung der genaueste), sonst null. */
export function categoryOf(groups: LibraryGroup[], p: { x: number; y: number }): LibraryGroup | null {
  let best: LibraryGroup | null = null;
  for (const g of groups) if (inGroup(g, p) && (!best || g.w * g.h < best.w * best.h)) best = g;
  return best;
}

/**
 * Freier Platz für eine Karte in einer Kategorie: zeilenweise im Raster des Rahmens, nicht auf einer Karte darin (`taken`) und nicht
 * in einem kleineren Rahmen, der darin liegt (`others`). Reicht der Rahmen nicht, wächst er nach unten (und auf eine Kartenbreite).
 */
export function slotInGroup(g: LibraryGroup, taken: { x: number; y: number }[], others: LibraryGroup[] = []): { x: number; y: number; w: number; h: number } {
  const w = Math.max(g.w, frameSize(1, 1).w);
  const columns = frameColumns(w);
  let i = 0;
  for (; i < 5000; i++) {
    const { x, y } = slotCenter({ ...g, w }, i);
    const h = Math.max(g.h, Math.round(y + CARD_HALF.h + SLOT.padX - g.y));
    const free = !taken.some((t) => Math.abs(t.x - x) < SLOT.w / 2 && Math.abs(t.y - y) < SLOT.h / 2);
    if (free && categoryOf([{ ...g, w, h }, ...others], { x, y })?.id === g.id) return { x: Math.round(x), y: Math.round(y), w, h };
  }
  const y = g.y + SLOT.top + CARD_HALF.h + Math.ceil(i / columns) * SLOT.h;
  return { x: Math.round(g.x + SLOT.padX + CARD_HALF.w), y: Math.round(y), w, h: Math.round(y + CARD_HALF.h + SLOT.padX - g.y) };
}

/**
 * Ein Rahmen wächst nach unten (auf Höhe `h`): Was darunter liegt – weitere Rahmen des Bereichs und gespeicherte Karten –, rückt um
 * denselben Betrag nach, damit nichts überdeckt wird (wie eine eingefügte Zeile). Liefert die neuen Lagen.
 */
export function growthShift(g: LibraryGroup, h: number, others: LibraryGroup[], placed: PlacedEntity[]): { frames: { id: string; y: number }[]; cards: { id: string; y: number }[] } {
  const dh = h - g.h;
  if (dh <= 0) return { frames: [], cards: [] };
  const bottom = g.y + g.h;
  return {
    frames: others.filter((f) => f.id !== g.id && f.y >= bottom - 1).map((f) => ({ id: f.id, y: f.y + dh })),
    cards: placed.filter((p) => p.placed && p.y > bottom).map((p) => ({ id: p.entity.id, y: p.y + dh })),
  };
}

/** Lage eines neuen Rahmens (zwei Karten breit): unter allem, was der Bereich auf der Pinnwand schon hat, links bündig. */
export function newCategoryFrame(groups: LibraryGroup[], placed: { x: number; y: number }[]): { x: number; y: number; w: number; h: number } {
  const size = frameSize(2, 1);
  const lefts = [...groups.map((g) => g.x), ...placed.map((p) => p.x - CARD_HALF.w - SLOT.padX)];
  const bottoms = [...groups.map((g) => g.y + g.h), ...placed.map((p) => p.y + CARD_HALF.h)];
  if (lefts.length === 0) return { x: 0, y: 0, ...size };
  return { x: Math.round(Math.min(...lefts)), y: Math.round(Math.max(...bottoms) + 60), ...size };
}

// ---------------------------------------------------------------------------
// Reihenfolge der Kategorien in der Kartenansicht (unabhängig von ihrer Lage auf der Pinnwand)
// ---------------------------------------------------------------------------

type Ordered = Pick<LibraryGroup, 'id' | 'name' | 'rank'>;

/** Kategorien in der Reihenfolge der Kartenansicht: nach Sortierschlüssel; ohne Schlüssel dahinter, nach Name. */
export function orderedGroups<T extends Ordered>(groups: T[]): T[] {
  return [...groups].sort((a, b) => {
    if (a.rank && b.rank && a.rank !== b.rank) return a.rank < b.rank ? -1 : 1;
    if (!a.rank !== !b.rank) return a.rank ? -1 : 1;
    return a.name.localeCompare(b.name, 'de') || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
  });
}

/** Sortierschlüssel für eine neue Kategorie: hinter allen anderen ihres Bereichs. */
export function groupRankAtEnd(groups: Ordered[]): string {
  const last = orderedGroups(groups.filter((g) => g.rank)).pop();
  return rankBetween(last?.rank ?? null, null);
}

/**
 * Neue Sortierschlüssel, wenn die Kategorie `id` vor `beforeId` rückt (null: ans Ende). Meist bekommt nur sie einen neuen Schlüssel
 * zwischen ihren neuen Nachbarn; fehlen Schlüssel oder ist dazwischen kein Platz, werden alle gleichmäßig neu verteilt. Leer, wenn
 * sich nichts ändert.
 */
export function reorderGroups(groups: Ordered[], id: string, beforeId: string | null): { id: string; rank: string }[] {
  const current = orderedGroups(groups);
  const moving = current.find((g) => g.id === id);
  if (!moving || beforeId === id) return [];
  const rest = current.filter((g) => g.id !== id);
  const found = beforeId === null ? -1 : rest.findIndex((g) => g.id === beforeId);
  const index = found < 0 ? rest.length : found;
  const next = [...rest.slice(0, index), moving, ...rest.slice(index)];
  if (next.every((g, i) => g.id === current[i]!.id)) return [];
  const prev = rest[index - 1] ?? null;
  const after = rest[index] ?? null;
  if (rest.every((g) => g.rank)) {
    try {
      return [{ id, rank: rankBetween(prev?.rank ?? null, after?.rank ?? null) }];
    } catch {
      // gleiche oder vertauschte Schlüssel: alle neu verteilen
    }
  }
  const ranks = evenRanks(next.length);
  return next.map((g, i) => ({ id: g.id, rank: ranks[i]! })).filter((u, i) => next[i]!.rank !== u.rank);
}
